import { Mark, mergeAttributes } from '@tiptap/core';
import Color from '@tiptap/extension-color';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import Italic from '@tiptap/extension-italic';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import StarterKit, { StarterKitOptions } from '@tiptap/starter-kit';
import { CustomNode } from '../components/subcomponents/collaborative-editor/collaborative-editor';
import { HIGHLIGHT_COLORS, HighlightType } from '../components/subcomponents/collaborative-editor/collaborative-editor-toolbar';
import { EditorWidgetPreview } from '../components/subcomponents/collaborative-editor/collaborative-editor-widget-preview';
import { NodeClassNames } from '../constants/NodeClassNames';

const isBlackColor = (color: string) => {
	return ['black', '#000000', '#000', 'rgb(0, 0, 0)', 'rgba(0, 0, 0, 1)'].includes(color);
};

const isWhiteColor = (color: string) => {
	return ['white', '#ffffff', '#fff', 'rgb(255, 255, 255)', 'rgba(255, 255, 255, 1)'].includes(color);
};

const isTransparent = (color: string) => {
	return ['transparent', 'rgba(0, 0, 0, 0)'].includes(color);
};

const isSpecialColorFormat = (color: string) => {
	// windowtext is a special color that is used in MS Word - explicitly set black color
	return ['windowtext', 'var(--clrSquiggleHighlightTextColor,#000000)'].includes(color);
};

const ALL_DATA_ATTRIBUTES = ['data-block', 'data-content'];

const CUSTOM_EXTENSIONS: CustomNode[] = [
	{
		name: 'smp_widget',
		group: 'block',
		atom: true,
		attributes: {
			...ALL_DATA_ATTRIBUTES.reduce((a, v) => ({ ...a, [v]: { default: null } }), {}),
		},
		parseHTMLDetails: [
			{
				tag: 'div',
				getAttrs: (node) => node instanceof HTMLElement && node.hasAttribute('data-block') && node.hasAttribute('data-content') && null,
			},
		],
		renderDetails: {
			tag: 'div',
		},
		component: EditorWidgetPreview,
	},
];

/*
 * Link extension is based on official Tiptap Link extension (MIT license): https://github.com/ueberdosis/tiptap/blob/main/packages/extension-link/src/link.ts
 */

export interface LinkOptions {
	/**
	 * A list of HTML attributes to be rendered.
	 */
	HTMLAttributes: Record<string, any>;
	/**
	 * A validation function that modifies link verification for the auto linker.
	 * @param url - The url to be validated.
	 * @returns - True if the url is valid, false otherwise.
	 */
	validate?: (url: string) => boolean;
}

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		link: {
			/**
			 * Set a link mark
			 */
			setLink: (attributes: { href: string; target?: string | null; class?: string }) => ReturnType;
			/**
			 * Toggle a link mark
			 */
			toggleLink: (attributes: { href: string; target?: string | null; class?: string }) => ReturnType;
			/**
			 * Unset a link mark
			 */
			unsetLink: () => ReturnType;
		};
	}
}

export const Link = Mark.create<LinkOptions>({
	name: 'link',

	priority: 1000,

	keepOnSplit: false,

	addOptions() {
		return {
			HTMLAttributes: {
				target: '_blank',
				class: null,
			},
			validate: undefined,
		};
	},

	addAttributes() {
		return {
			href: {
				default: null,
			},
			target: {
				default: this.options.HTMLAttributes.target,
			},
			class: {
				default: this.options.HTMLAttributes.class,
			},
			'data-resource-id': {
				default: null,
			},
			'data-resource-type': {
				default: null,
			},
			'data-sport': {
				default: null,
			},
		};
	},

	parseHTML() {
		return [{ tag: 'a[href]:not([href *= "javascript:" i])' }];
	},

	renderHTML({ HTMLAttributes }) {
		return ['a', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
	},

	addCommands() {
		return {
			setLink: (attributes) => ({ chain }) => {
				return chain().setMark(this.name, attributes).setMeta('preventAutolink', true).run();
			},

			toggleLink: (attributes) => ({ chain }) => {
				return chain().toggleMark(this.name, attributes, { extendEmptyMarkRange: true }).setMeta('preventAutolink', true).run();
			},

			unsetLink: () => ({ chain }) => {
				return chain().unsetMark(this.name, { extendEmptyMarkRange: false }).setMeta('preventAutolink', true).run();
			},
		};
	},
});

export const CustomHighlight = Highlight.extend({
	parseHTML() {
		return [
			{
				tag: 'span',
				getAttrs: (element) => {
					if (!(element instanceof HTMLElement)) {
						return false;
					}

					const hasStyles = element.hasAttribute('style');

					if (!hasStyles) {
						return false;
					}

					const backgroundColor = element.style.backgroundColor || element.style.background;

					if (backgroundColor && !isWhiteColor(backgroundColor) && !isBlackColor(backgroundColor) && !isTransparent(backgroundColor)) {
						return {
							'data-color': backgroundColor,
						};
					}

					return false;
				},
			},
			{
				tag: 'mark',
			},
		];
	},
	addAttributes() {
		if (!this.options.multicolor) {
			return {};
		}

		return {
			color: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-color') || element.style.backgroundColor || element.style.background,
				renderHTML: (attributes) => {
					if (!attributes.color) {
						return {};
					}

					return {
						'data-color': attributes.color,
						style: `background-color: ${attributes.color};`,
					};
				},
			},
			class: {
				default: null,
				parseHTML: (element) => element.getAttribute('class'),
				renderHTML: (attributes) => {
					if (!attributes.class) {
						return {};
					}

					return {
						class: attributes.class,
					};
				},
			},
		};
	},
	renderHTML({ HTMLAttributes }) {
		const additionalAttributes: Record<string, any> = {};
		const relevantColor = HIGHLIGHT_COLORS.find(
			({ label, color, type }) =>
				type === HighlightType.MARKER &&
				(color === HTMLAttributes['data-color'] || label === (HTMLAttributes.class ? HTMLAttributes.class.split('-')[1] : null)),
		);

		if (relevantColor) {
			if (!HTMLAttributes['data-color']) {
				additionalAttributes['data-color'] = relevantColor.color;
				additionalAttributes.style = `background-color: ${relevantColor.color}; color: inherit`;
			}

			if (!HTMLAttributes.class) {
				additionalAttributes.class = 'marker-' + relevantColor.label;
			}
		}

		return ['mark', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, additionalAttributes), 0];
	},
});

export const CustomColor = Color.extend({
	addGlobalAttributes() {
		return [
			{
				types: this.options.types,
				attributes: {
					color: {
						default: null,
						parseHTML: (element) => {
							if (element.style.color) {
								return element.style.color.replace(/['"]+/g, '');
							}
						},
						renderHTML: (attributes) => {
							if (!attributes.color) {
								return {
									style: 'color: inherit; background-color: inherit;',
								};
							}

							return {
								style: `color: ${attributes.color}; background-color: inherit;`,
							};
						},
					},
					class: {
						default: null,
						renderHTML: (attributes) => {
							if (!attributes.class) {
								return {};
							}

							return {
								class: attributes.class,
							};
						},
					},
				},
			},
		];
	},
});

const CustomTextStyle = TextStyle.extend({
	priority: 1000,
	parseHTML() {
		return [
			{
				tag: 'span',
				getAttrs: (element) => {
					if (!(element instanceof HTMLElement)) {
						return false;
					}

					const hasStyles = element.hasAttribute('style');

					if (!hasStyles) {
						return false;
					}

					if (
						element.style.color &&
						!isBlackColor(element.style.color) &&
						!isWhiteColor(element.style.color) &&
						!isSpecialColorFormat(element.style.color)
					) {
						return {};
					}

					return false;
				},
			},
			{
				tag: 'mark',
				getAttrs: (element) => {
					if (!(element instanceof HTMLElement)) {
						return false;
					}

					const relevantClassName = [...element.classList].find((className) => className.startsWith('pen-'));

					if (relevantClassName) {
						const color = relevantClassName.split('-')[1];
						const relevantColor = HIGHLIGHT_COLORS.find(({ label, type }) => type === HighlightType.PEN && label === color);

						if (relevantColor) {
							return { class: relevantClassName, color: relevantColor.color };
						}

						return {};
					} else if (
						element.hasAttribute('style') &&
						element.style.color &&
						!isBlackColor(element.style.color) &&
						!isWhiteColor(element.style.color) &&
						!isSpecialColorFormat(element.style.color)
					) {
						return { color: element.style.color };
					}

					return false;
				},
			},
		];
	},

	renderHTML({ HTMLAttributes, mark }) {
		const relevantColor = HIGHLIGHT_COLORS.find(({ color, type }) => type === HighlightType.PEN && color === mark.attrs.color);

		return [
			'mark',
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
				class: HighlightType.PEN + (relevantColor ? `-${relevantColor.label}` : ''),
			}),
			0,
		];
	},
});

const CustomHeading = Heading.extend({
	addAttributes() {
		return {
			level: {
				default: 1,
				rendered: false,
			},
			id: {
				default: null,
			},
			tabindex: {
				default: null,
			},
		};
	},
});

const CustomItalic = Italic.extend({
	parseHTML() {
		return [{ tag: 'i' }];
	},

	renderHTML({ HTMLAttributes }) {
		return ['i', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
	},
});

export const getDefaultExtensions = (enableCode = true) => {
	const codeExtensionsConfig: { code?: StarterKitOptions['code']; codeBlock?: StarterKitOptions['codeBlock'] } = {};

	if (!enableCode) {
		codeExtensionsConfig.code = false;
		codeExtensionsConfig.codeBlock = false;
	}

	return [
		CustomColor,
		CustomHeading,
		CustomHighlight.configure({ multicolor: true }),
		CustomItalic,
		Link.configure({ HTMLAttributes: { class: NodeClassNames.LINK } }),
		Table,
		TableCell,
		TableHeader,
		TableRow,
		TextAlign.configure({
			types: ['heading', 'paragraph'],
			alignments: ['left', 'right', 'center', 'justify'],
		}),
		CustomTextStyle,
		Underline,
		StarterKit.configure({
			...codeExtensionsConfig,
			bulletList: {
				keepMarks: false,
				keepAttributes: false,
			},
			orderedList: {
				keepMarks: false,
				keepAttributes: false,
			},
			heading: false,
			italic: false,
		}),
	];
};

export default CUSTOM_EXTENSIONS;
